/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import {
  ListicleStoryTemplate,
  LiveBlogStoryTemplate,
  PhotoStoryTemplates,
  VideoStoryTemplate,
  TextStoryTemplate
} from '@quintype/arrow';

import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';
import { StoryFooter } from '../molecules/story-footer';
import { getStoryDataWithAdSlots } from './get-story';

const wrapper =
  StoryComponent =>
    ({
      story,
      templateConfig,
      preview,
      storyElementsConfig,
      adComponent,
      widgetComp,
      enableDarkMode,
      loadRelatedStories,
      staticRelatedStories,
      customAsideCollections
    }) => {
      return (
        <StoryComponent
          story={story}
          config={templateConfig}
          storyElementsConfig={storyElementsConfig}
          adComponent={adComponent}
          widgetComp={widgetComp}
          enableDarkMode={enableDarkMode}
          loadRelatedStories={loadRelatedStories}
          staticRelatedStories={staticRelatedStories}
          customAsideCollections={customAsideCollections}
          secondChild={
            !preview && (
              <StoryFooter
                story={story}
                isInfiniteScrollEnabled={get(templateConfig, ['isStoryInfiniteScrollEnabled'], false)}
              />
            )
          }
        />
      );
    };

const StoryTemplate = ({
  subPageType,
  story,
  templateConfig,
  preview,
  storyElementsConfig,
  adComponent,
  widgetComp,
  enableDarkMode,
  loadRelatedStories,
  staticRelatedStories,
  customAsideCollections
}) => {
  switch (subPageType) {
    case 'video':
      return wrapper(VideoStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp,
        enableDarkMode,
        loadRelatedStories,
        staticRelatedStories,
        customAsideCollections
      });
    case 'photo':
      return wrapper(PhotoStoryTemplates)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp,
        enableDarkMode,
        loadRelatedStories,
        staticRelatedStories,
        customAsideCollections
      });
    case 'listicle':
      return wrapper(ListicleStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp,
        enableDarkMode,
        loadRelatedStories,
        staticRelatedStories,
        customAsideCollections
      });
    case 'live-blog':
      return wrapper(LiveBlogStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp,
        enableDarkMode,
        loadRelatedStories,
        staticRelatedStories,
        customAsideCollections
      });
    case 'text':
    default:
      return wrapper(TextStoryTemplate)({
        story,
        templateConfig,
        preview,
        storyElementsConfig,
        adComponent,
        widgetComp,
        enableDarkMode,
        loadRelatedStories,
        staticRelatedStories,
        customAsideCollections
      });
  }
};

const PublicStory = ({
  story,
  templateConfig,
  preview,
  storyElementsConfig,
  adComponent,
  widgetComp,
  storyBaseType,
  enableDarkMode,
  loadRelatedStories,
  config,
  staticRelatedStories,
  customAsideCollections
}) => {
  const sketchesHost = get(config, ['sketches-host'], '');
  const subPageType = storyBaseType || story['story-template'];
  const storyId = get(story, ['id'], '');
  const { storySlots = [] } = templateConfig;
  const isBlogClosed = get(story, ['metadata', 'is-closed'], false);

  const [currentStory, setCurrentStory] = useState(story);
  const latestUpdatedAtRef = useRef(story['updated-at']);

  const storyDataLoader = () => getStoryDataWithAdSlots(storyId, storySlots, sketchesHost);

  useEffect(() => {
    if (subPageType !== 'live-blog') return;
    if (subPageType === 'live-blog' && isBlogClosed) return;

    const interval = setInterval(async () => {
      try {
        const updatedData = await storyDataLoader();
        const updatedStory = updatedData?.story;
        if (latestUpdatedAtRef.current !== updatedStory?.['updated-at']) {
          latestUpdatedAtRef.current = updatedStory['updated-at'];
          setCurrentStory(prev => ({
            ...updatedStory,
            firstStory: prev?.firstStory,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch updated story:', err);
      }
    }, 20000);

    return () => clearInterval(interval);
  }, [subPageType, storyId, isBlogClosed]);

  return StoryTemplate({
    subPageType,
    story: currentStory,
    templateConfig,
    preview,
    storyElementsConfig,
    adComponent,
    widgetComp,
    enableDarkMode,
    loadRelatedStories,
    staticRelatedStories,
    customAsideCollections
  });
};

PublicStory.propTypes = {
  story: PropTypes.object,
  templateConfig: PropTypes.object,
  preview: PropTypes.bool,
  storyElementsConfig: PropTypes.object,
  storyBaseType: PropTypes.string,
  staticRelatedStories: PropTypes.array,
  customAsideCollections: PropTypes.object
};

export default PublicStory;
